import { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import {
  TextInput,
  Text,
  Paper,
  Group,
  Button,
  Divider,
  Checkbox,
  Anchor,
  Stack,
  Container,
  Alert,
  Badge,
  NumberFormatter,
} from "@mantine/core";
import { IconGift, IconInfoCircle, IconMailCheck } from "@tabler/icons-react";
import { GoogleButton } from "../_Shared/GoogleButton";
import { userRegister } from "./authApi";
import { useStore } from "../store";
import { Link, useLocation, useSearch } from "wouter";
import { socialLoginFactory } from "./socialLogin";
import { useTranslation } from "react-i18next";
import { jsonGet } from "../api";
import LangSelector from "./LangSelector";
import { rememberReturnTo, returnToFromUrl } from "./returnTo";

export default function RegisterForm({ params, realm, touLink, hideGoogle, hideLangSelector, ...props }) {
  const [registeredEmail, setRegisteredEmail] = useState(null);
  const [isFetching, setIsFetching] = useState(null); // null | "email" | "google"
  const [errorMessage, setErrorMessage] = useState(null);
  const [signupCodeInfo, setSignupCodeInfo] = useState(null);
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);
  const signupCode = searchParams.get("code") || "";

  const { t } = useTranslation("Auth");

  const form = useForm({
    initialValues: {
      realm: realm,
      email: "",
      name: "",
      terms: false,
      invitationToken: "",
      signupCode: signupCode,
    },
    validate: {
      email: (val) =>
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val.trim())
          ? null
          : t("invalid_email"),
    },
  });

  const authToken = useStore((state) => state.authToken);
  const profile = useStore((state) => state.profile);
  const userLoggedIn = useStore((state) => state.userLoggedIn);
  const [, setLocation] = useLocation();

  // The popup posts { access_token, ... } — the same shape a password login returns.
  const socialLogin = socialLoginFactory(
    async (event) => {
      await userLoggedIn(event.data);
    },
    () => setIsFetching(null),
  );

  // Email signup continues in the activation mail, which opens a new page without this
  // query string. Remember where the visitor wanted to go (e.g. a supplier invitation).
  // Runs before the redirect below, which changes the URL.
  useEffect(() => {
    const returnTo = returnToFromUrl();
    if (returnTo) rememberReturnTo(returnTo);
  }, []);

  // Signed in already: nothing to register. A token alone is not enough: an old token from an
  // earlier visit stays in storage until the first request fails.
  useEffect(() => {
    if (authToken && profile) setLocation("/");
  }, [authToken, profile]);

  // Validate signup code on mount
  useEffect(() => {
    if (signupCode) {
      jsonGet(`signup-codes/${encodeURIComponent(signupCode)}/validity`)
        .then((data) => {
          if (data?.valid) {
            setSignupCodeInfo(data);
          }
        })
        .catch(() => {});
    }
  }, [signupCode]);

  const loginHref = `/auth/login${searchString ? `?${searchString}` : ""}`;

  if (registeredEmail) {
    return (
      <Container size="xs" mt="xl">
        <Paper radius="md" p="xl" withBorder {...props}>
          <Group gap="sm" mb="md" wrap="nowrap">
            <IconMailCheck size={28} stroke={1.5} />
            <Text size="lg" fw={500}>
              {t("register_success_title", "Check your inbox")}
            </Text>
          </Group>
          <Text>
            {t("register_success_message", {
              email: registeredEmail,
              defaultValue:
                "We sent an activation link to {{email}}. Open it to set your password and start.",
            })}
          </Text>
          <Text mt="md" size="sm" c="dimmed">
            {t(
              "register_success_hint",
              "No mail after a few minutes? Check your spam folder. Or submit the form again with the same address to get a new link.",
            )}
          </Text>
          <Group justify="space-between" mt="xl">
            <Anchor
              component="button"
              type="button"
              size="xs"
              c="dimmed"
              onClick={() => setRegisteredEmail(null)}
            >
              {t("register_use_other_email", "Use a different address")}
            </Anchor>
            <Anchor component={Link} href={loginHref} size="xs" c="dimmed">
              {t("login_instead")}
            </Anchor>
          </Group>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="xs" mt="xl">
      {errorMessage && (
        <Alert
          variant="light"
          color="red"
          title={t("register_failed", "Registration failed")}
          mb="lg"
          icon={<IconInfoCircle />}
        >
          {errorMessage}
        </Alert>
      )}

      <Paper radius="md" p="xl" withBorder {...props}>
        <Group align="flex-start" justify="space-between">
          <Text size="lg" fw={500}>
            {t("register_title", "Create your account")}
          </Text>
          {!hideLangSelector && <LangSelector />}
        </Group>

        {signupCodeInfo && (
          <Alert
            color="teal"
            mt="md"
            icon={<IconGift size={18} />}
            title="Bonus credits included"
          >
            {signupCodeInfo.description || "Your signup code includes"}{" "}
            <Badge color="teal" variant="light" size="lg">
              <NumberFormatter
                value={signupCodeInfo.creditsToGrant / 100}
                thousandSeparator
              />{" "}
              free SKUs
            </Badge>
          </Alert>
        )}

        {!hideGoogle && (
          <Group grow mb="md" mt="md">
            <GoogleButton
              loading={isFetching === "google"}
              disabled={!!isFetching}
              onClick={() => {
                setErrorMessage(null);
                setIsFetching("google");
                socialLogin("Google", params?.token, signupCode || null);
              }}
              radius="xl"
            >
              Google
            </GoogleButton>
          </Group>
        )}

        {!hideGoogle && (
          <Divider
            label={t("or_continue_with_email", "Or continue with email")}
            labelPosition="center"
            my="lg"
          />
        )}

        <form
          onSubmit={form.onSubmit((values) => {
            const email = values.email.trim();
            setIsFetching("email");
            setErrorMessage(null);
            userRegister({ ...values, email, name: values.name.trim() })
              .then(() => setRegisteredEmail(email))
              .catch((err) => {
                setErrorMessage(
                  err.status === 429
                    ? t(
                        "register_rate_limited",
                        "Too many attempts. Please wait a minute and try again.",
                      )
                    : t(
                        "register_error",
                        "We could not create your account. Please try again in a moment.",
                      ),
                );
              })
              .finally(() => setIsFetching(null));
          })}
        >
          <Stack mt={hideGoogle ? "md" : undefined}>
            <TextInput
              required
              disabled={!!isFetching}
              label={t("name")}
              placeholder={t("name_placeholder")}
              value={form.values.name}
              onChange={(event) =>
                form.setFieldValue("name", event.currentTarget.value)
              }
              radius="md"
            />

            <TextInput
              required
              disabled={!!isFetching}
              type="email"
              autoComplete="email"
              label={t("email")}
              placeholder={t("email_placeholder")}
              value={form.values.email}
              onChange={(event) =>
                form.setFieldValue("email", event.currentTarget.value)
              }
              error={form.errors.email}
              radius="md"
            />

            <Checkbox
              required
              disabled={!!isFetching}
              label={
                <>
                  {t("accept_terms_prefix", "I accept the")}{" "}
                  <Anchor target="_blank" href={touLink} inherit>
                    {t("terms_link", "terms and conditions")}
                  </Anchor>
                </>
              }
              checked={form.values.terms}
              onChange={(event) =>
                form.setFieldValue("terms", event.currentTarget.checked)
              }
            />
          </Stack>

          <Group justify="space-between" mt="xl">
            <Anchor href={loginHref} component={Link} c="dimmed" size="xs">
              {t("have_account_login", "Already have an account? Log in")}
            </Anchor>
            <Button
              type="submit"
              radius="xl"
              loading={isFetching === "email"}
              disabled={!!isFetching}
            >
              {t("register")}
            </Button>
          </Group>
        </form>
      </Paper>
    </Container>
  );
}
